(function () {
  "use strict";

  if (window.MarineMaxCoveoFilterRequestPlanner) return;

  const REQUIRED_FUNCTION_DEPENDENCIES = Object.freeze([
    "coveoPriceFacetApiScale",
    "facetIdsMatchLength",
    "facetIdsMatchPrice",
    "facetIdsMatchYear",
    "facetItemNumericBounds",
    "getCoveoFacetRequestFieldId",
    "getCoveoFriendlyUrlFacetKey",
    "isNumericalRangeFacet",
    "requestFacetMatchesFacetId",
    "toCoveoPriceFacetApiValue",
    "tryFacetNumericDomain",
  ]);
  const FACET_SELF_SELECTION_KEYS = Object.freeze([
    "values",
    "currentValues",
    "domain",
    "facetDomain",
    "range",
  ]);
  const PRICE_DOMAIN_CENTS_THRESHOLD = 1000000000;

  function createCoveoFilterRequestPlanner(dependencies) {
    const deps = dependencies || {};
    const missingDependencies = REQUIRED_FUNCTION_DEPENDENCIES.filter(
      function (name) {
        return typeof deps[name] !== "function";
      },
    );
    const yearFacetId = String(deps.yearFacetId || "").trim();
    if (!yearFacetId) missingDependencies.push("yearFacetId");
    const initialManagedRangeFacets = Array.isArray(
      deps.initialManagedRangeFacets,
    )
      ? deps.initialManagedRangeFacets
          .map(function (descriptor) {
            const facetId = String(descriptor?.facetId || "").trim();
            if (!facetId) return null;
            return Object.freeze({
              displayName: String(descriptor?.displayName || facetId),
              facetId,
              interval: String(descriptor?.interval || ""),
            });
          })
          .filter(Boolean)
      : [];
    if (!initialManagedRangeFacets.length) {
      missingDependencies.push("initialManagedRangeFacets");
    }
    if (missingDependencies.length) {
      throw new Error(
        `[Coveo Filter Request Planner] Missing dependencies: ${missingDependencies.join(", ")}`,
      );
    }

    const {
      coveoPriceFacetApiScale,
      facetIdsMatchLength,
      facetIdsMatchPrice,
      facetIdsMatchYear,
      facetItemNumericBounds,
      getCoveoFacetRequestFieldId,
      getCoveoFriendlyUrlFacetKey,
      isNumericalRangeFacet,
      requestFacetMatchesFacetId,
      toCoveoPriceFacetApiValue,
      tryFacetNumericDomain,
    } = deps;

    function buildSyntheticYearFacetRequest(selection) {
      if (!selection || selection.fullRange) return null;
      const lo = Math.round(Number(selection.lo));
      const hi = Math.round(Number(selection.hi));
      if (!Number.isFinite(lo) || !Number.isFinite(hi)) return null;
      return {
        facetId: yearFacetId,
        displayName: "Year",
        field: yearFacetId,
        type: "numericalRange",
        values: [
          {
            start: Math.min(lo, hi),
            end: Math.max(lo, hi),
            endInclusive: true,
            state: "selected",
          },
        ],
        freezeCurrentValues: false,
        preventAutoSelect: false,
        numberOfValues: 1,
        initialNumberOfValues: 1,
        interval: "continuous",
      };
    }

    function buildSyntheticManagedRangeFacetRequest(descriptor, range) {
      const facetId = String(descriptor?.facetId || "").trim();
      if (!facetId) return null;
      const rangeValue = buildPendingRangeValue(
        facetId,
        {
          facetId,
          field: facetId,
          type: "numericalRange",
        },
        range,
      );
      if (!rangeValue) return null;
      const facet = {
        facetId,
        displayName: String(descriptor?.displayName || facetId),
        field: facetId,
        type: "numericalRange",
        values: [rangeValue],
        freezeCurrentValues: false,
        preventAutoSelect: false,
        numberOfValues: 1,
        initialNumberOfValues: 1,
      };
      const interval = String(descriptor?.interval || "").trim();
      if (interval) facet.interval = interval;
      return facet;
    }

    function collectFacetRequestNumericCandidates(facet) {
      const candidates = [];
      const facetState =
        facet?.state && typeof facet.state === "object" ? facet.state : null;
      [
        facet?.values,
        facet?.currentValues,
        facetState?.values,
        facetState?.currentValues,
      ].forEach(function (valueList) {
        if (!Array.isArray(valueList)) return;
        valueList.forEach(function (value) {
          const bounds = facetItemNumericBounds(value, {
            allowNumericValueFallback: true,
          });
          if (!bounds) return;
          candidates.push(bounds.lo, bounds.hi);
        });
      });
      const domain = tryFacetNumericDomain(facetState || facet || {});
      if (domain) candidates.push(domain.lo, domain.hi);
      return candidates;
    }

    function resolveRangeKind(facetId, facetLike) {
      if (facetIdsMatchYear(facetId)) return "year";
      if (facetIdsMatchPrice(facetId)) return "price";
      if (facetIdsMatchLength(facetId)) return "length";
      if (isNumericalRangeFacet(facetLike)) return "numeric";
      return "";
    }

    function buildPendingRangeValue(facetId, facetLike, range) {
      const kind = resolveRangeKind(facetId, facetLike);
      const lo = Number(range?.lo);
      const hi = Number(range?.hi);
      if (!kind || !Number.isFinite(lo) || !Number.isFinite(hi)) return null;

      const normalizedLo =
        kind === "year" || kind === "length" || kind === "numeric"
          ? Math.round(Math.min(lo, hi))
          : Math.min(lo, hi);
      const normalizedHi =
        kind === "year" || kind === "length" || kind === "numeric"
          ? Math.round(Math.max(lo, hi))
          : Math.max(lo, hi);
      const priceApiScale =
        kind === "price"
          ? coveoPriceFacetApiScale(
              collectFacetRequestNumericCandidates(facetLike),
            )
          : 1;

      return {
        start:
          kind === "price"
            ? toCoveoPriceFacetApiValue(normalizedLo, priceApiScale)
            : normalizedLo,
        end:
          kind === "price"
            ? toCoveoPriceFacetApiValue(normalizedHi, priceApiScale)
            : normalizedHi,
        endInclusive: true,
        state: "selected",
      };
    }

    function buildPendingRegularFacetRequest(facetId, values) {
      const normalizedFacetId = String(facetId || "").trim();
      const normalizedValues = Array.from(
        new Set(
          (Array.isArray(values) ? values : [])
            .map(function (value) {
              return String(value || "").trim();
            })
            .filter(Boolean),
        ),
      );
      if (!normalizedFacetId || !normalizedValues.length) return null;

      const friendlyKey = getCoveoFriendlyUrlFacetKey(normalizedFacetId);
      const displayNameByFriendlyKey = {
        make: "Make",
        model: "Model",
        condition: "Condition",
        "boat-type": "Boat Type",
        availability: "Availability",
        "engine-type": "Engine Type",
        promotions: "Promotion",
      };
      return {
        facetId: normalizedFacetId,
        displayName:
          displayNameByFriendlyKey[friendlyKey] || normalizedFacetId,
        field: normalizedFacetId,
        type: "regular",
        values: normalizedValues.map(function (value) {
          return { value, state: "selected" };
        }),
        freezeCurrentValues: false,
        preventAutoSelect: false,
        numberOfValues: 10,
        initialNumberOfValues: 10,
      };
    }

    function getSelectedRequestFacetValues(body, matcher) {
      if (!Array.isArray(body?.facets) || typeof matcher !== "function") {
        return [];
      }
      const selected = [];
      body.facets.forEach(function (facet) {
        const facetId = getCoveoFacetRequestFieldId(facet);
        if (!facetId || !matcher(facetId)) return;
        [facet.values, facet.currentValues].forEach(function (values) {
          if (!Array.isArray(values)) return;
          values.forEach(function (value) {
            if (String(value?.state || "").toLowerCase() !== "selected") {
              return;
            }
            const raw = String(
              value?.value ?? value?.rawValue ?? value?.displayValue ?? "",
            ).trim();
            if (raw && !selected.includes(raw)) selected.push(raw);
          });
        });
      });
      return selected;
    }

    /*
     * Coveo Query Pipelines support one Make context value. Multiple selected
     * makes deliberately clear urlMake so the context cannot narrow a request
     * differently from its facets. Return a plan so the runtime remains the
     * only layer that mutates the Commerce request body.
     */
    function planUrlMakeContext(options) {
      const config = options || {};
      const makeValues = config.makeValues || [];
      const hasObjectContext = !!(
        config.context && typeof config.context === "object"
      );
      const currentContext = hasObjectContext ? config.context : {};
      const currentCustom =
        currentContext.custom && typeof currentContext.custom === "object"
          ? currentContext.custom
          : {};
      const nextCustom = Object.assign({}, currentCustom);
      if (makeValues.length === 1) {
        nextCustom.urlMake = makeValues[0];
      } else {
        delete nextCustom.urlMake;
      }
      if (
        JSON.stringify(nextCustom) === JSON.stringify(currentCustom) &&
        hasObjectContext
      ) {
        return {
          changed: false,
          context: config.context,
        };
      }
      return {
        changed: true,
        context: Object.assign({}, currentContext, {
          custom: nextCustom,
        }),
      };
    }

    function planFacetOnlyTargetSelfSelection(options) {
      const config = options || {};
      const sourceFacets = Array.isArray(config.facets) ? config.facets : [];
      const targetFacetId = config.targetFacetId;
      if (!targetFacetId) {
        return { changed: false, facets: sourceFacets };
      }

      let changed = false;
      const nextFacets = sourceFacets.map(function (facet) {
        if (!requestFacetMatchesFacetId(facet, targetFacetId)) return facet;
        const hasSelfSelection = FACET_SELF_SELECTION_KEYS.some(function (key) {
          return Object.prototype.hasOwnProperty.call(facet, key);
        });
        if (!hasSelfSelection) return facet;

        const nextFacet = Object.assign({}, facet);
        FACET_SELF_SELECTION_KEYS.forEach(function (key) {
          delete nextFacet[key];
        });
        changed = true;
        return nextFacet;
      });

      return {
        changed,
        facets: changed ? nextFacets : sourceFacets,
      };
    }

    function planManagedRangeTargetRemoval(options) {
      const config = options || {};
      const sourceFacets = Array.isArray(config.facets) ? config.facets : [];
      const targetFacetId = config.targetFacetId;
      if (!targetFacetId) {
        return { changed: false, facets: sourceFacets };
      }

      const nextFacets = sourceFacets.filter(function (facet) {
        return !requestFacetMatchesFacetId(facet, targetFacetId);
      });
      const changed = nextFacets.length !== sourceFacets.length;
      return {
        changed,
        facets: changed ? nextFacets : sourceFacets,
      };
    }

    function facetRequestLooksLikePrice(facet) {
      const id = getCoveoFacetRequestFieldId(facet);
      return (
        id === "ec_price" ||
        id === "price_display" ||
        id.includes("ec_price") ||
        id.includes("price_display") ||
        id.includes("promo_price") ||
        id.includes("listprice") ||
        (id.includes("price") &&
          !id.includes("pricebook") &&
          !id.includes("precision"))
      );
    }

    function planPriceFacetDomainSanitization(options) {
      const config = options || {};
      const sourceFacets = Array.isArray(config.facets) ? config.facets : [];
      const maxDomain = Number(config.maxDomain);
      if (!Number.isFinite(maxDomain)) {
        return { changed: false, facets: sourceFacets };
      }

      let changed = false;
      const nextFacets = sourceFacets.map(function (facet) {
        if (!facet || typeof facet !== "object") return facet;
        if (!facetRequestLooksLikePrice(facet)) return facet;
        const domain = facet.domain;
        if (!domain || typeof domain !== "object") return facet;

        const max = Number(domain.max);
        const min = Number(domain.min);
        if (!Number.isFinite(max) || max <= maxDomain) return facet;

        const nextFacet = Object.assign({}, facet);
        if (Math.abs(max) >= PRICE_DOMAIN_CENTS_THRESHOLD) {
          const nextDomain = Object.assign({}, domain, { max: max / 100 });
          if (Number.isFinite(min)) nextDomain.min = min / 100;
          if (Number(nextDomain.max) <= maxDomain) {
            nextFacet.domain = nextDomain;
            changed = true;
            return nextFacet;
          }
        }

        delete nextFacet.domain;
        changed = true;
        return nextFacet;
      });

      return {
        changed,
        facets: changed ? nextFacets : sourceFacets,
      };
    }

    function planEmptyFacetValuesSanitization(options) {
      const config = options || {};
      const sourceFacets = Array.isArray(config.facets) ? config.facets : [];
      const dropEmptyFacets = !!config.dropEmptyFacets;
      const preserveFacetId = String(config.preserveFacetId || "");
      let changed = false;

      if (dropEmptyFacets) {
        const nextFacets = [];
        sourceFacets.forEach(function (facet) {
          if (!facet || typeof facet !== "object") {
            changed = true;
            return;
          }
          if (
            preserveFacetId &&
            requestFacetMatchesFacetId(facet, preserveFacetId)
          ) {
            if (Array.isArray(facet.values) && facet.values.length === 0) {
              const nextFacet = Object.assign({}, facet);
              delete nextFacet.values;
              nextFacets.push(nextFacet);
              changed = true;
              return;
            }
            nextFacets.push(facet);
            return;
          }
          if (Array.isArray(facet.values) && facet.values.length > 0) {
            nextFacets.push(facet);
            return;
          }
          changed = true;
        });
        if (nextFacets.length !== sourceFacets.length) changed = true;
        return {
          changed,
          facets: changed ? nextFacets : sourceFacets,
        };
      }

      const nextFacets = sourceFacets.map(function (facet) {
        if (!facet || typeof facet !== "object") return facet;
        if (!Array.isArray(facet.values) || facet.values.length !== 0) {
          return facet;
        }
        const nextFacet = Object.assign({}, facet);
        delete nextFacet.values;
        changed = true;
        return nextFacet;
      });
      return {
        changed,
        facets: changed ? nextFacets : sourceFacets,
      };
    }

    function planPendingFacetRequests(options) {
      const config = options || {};
      const sourceFacets = Array.isArray(config.facets) ? config.facets : [];
      const pendingRegularEntries = Array.isArray(
        config.pendingRegularEntries,
      )
        ? config.pendingRegularEntries
        : [];
      const resolveFacetRange =
        typeof config.resolveFacetRange === "function"
          ? config.resolveFacetRange
          : function () {
              return null;
            };
      const resolveFacetValues =
        typeof config.resolveFacetValues === "function"
          ? config.resolveFacetValues
          : function () {
              return [];
            };
      const synthesizeMissingManagedRanges =
        config.synthesizeMissingManagedRanges === true;

      const nextFacets = [];
      const appliedFacetIds = new Set();
      const selectionEffects = [];
      let hasYearFacetRequest = false;
      let pendingYearRange = null;

      sourceFacets.forEach(function (facet) {
        if (!facet || typeof facet !== "object") {
          nextFacets.push(facet);
          return;
        }

        const facetId = getCoveoFacetRequestFieldId(facet);
        if (!facetId) {
          nextFacets.push(facet);
          return;
        }

        const nextFacet = Object.assign({}, facet);
        const pendingRange = resolveFacetRange(facetId);
        const pendingValues = resolveFacetValues(facetId);

        if (pendingRange) {
          const rangeValue = buildPendingRangeValue(
            facetId,
            facet,
            pendingRange,
          );
          if (rangeValue) {
            nextFacet.values = [rangeValue];
            appliedFacetIds.add(facetId);
          }
          if (facetIdsMatchYear(facetId)) {
            pendingYearRange = pendingRange;
            hasYearFacetRequest = true;
            selectionEffects.push({
              kind: "year",
              selection: {
                lo: Math.round(pendingRange.lo),
                hi: Math.round(pendingRange.hi),
                fullRange: false,
              },
            });
          } else {
            selectionEffects.push({
              kind: "range",
              facetId,
              range: pendingRange,
            });
          }
          nextFacets.push(nextFacet);
          return;
        }

        if (Array.isArray(pendingValues) && pendingValues.length) {
          nextFacet.values = pendingValues.map(function (value) {
            return { value, state: "selected" };
          });
          appliedFacetIds.add(facetId);
        }
        nextFacets.push(nextFacet);
      });

      if (synthesizeMissingManagedRanges) {
        initialManagedRangeFacets.forEach(function (descriptor) {
          const alreadyIncluded = nextFacets.some(function (facet) {
            return requestFacetMatchesFacetId(facet, descriptor.facetId);
          });
          if (alreadyIncluded) return;
          const pendingRange = resolveFacetRange(descriptor.facetId);
          if (!pendingRange) return;
          const syntheticFacet = buildSyntheticManagedRangeFacetRequest(
            descriptor,
            pendingRange,
          );
          if (!syntheticFacet) return;
          nextFacets.push(syntheticFacet);
          appliedFacetIds.add(descriptor.facetId);
          selectionEffects.push({
            kind: "range",
            facetId: descriptor.facetId,
            range: pendingRange,
          });
        });
      }

      pendingRegularEntries.forEach(function (entry) {
        const alreadyIncluded = nextFacets.some(function (facet) {
          return requestFacetMatchesFacetId(facet, entry?.facetId);
        });
        if (alreadyIncluded) return;
        const syntheticFacet = buildPendingRegularFacetRequest(
          entry?.facetId,
          entry?.values,
        );
        if (!syntheticFacet) return;
        nextFacets.push(syntheticFacet);
        appliedFacetIds.add(entry.facetId);
      });

      pendingYearRange =
        pendingYearRange || resolveFacetRange(yearFacetId);
      if (pendingYearRange && !hasYearFacetRequest) {
        const selection = {
          lo: Math.round(pendingYearRange.lo),
          hi: Math.round(pendingYearRange.hi),
          fullRange: false,
        };
        const syntheticYearFacet = buildSyntheticYearFacetRequest(selection);
        if (syntheticYearFacet) {
          nextFacets.push(syntheticYearFacet);
          appliedFacetIds.add(yearFacetId);
          selectionEffects.push({ kind: "year", selection });
        }
      }

      return {
        facets: nextFacets,
        appliedFacetIds: Array.from(appliedFacetIds),
        selectionEffects,
      };
    }

    return Object.freeze({
      buildPendingRangeValue,
      buildPendingRegularFacetRequest,
      buildSyntheticManagedRangeFacetRequest,
      buildSyntheticYearFacetRequest,
      getSelectedRequestFacetValues,
      planEmptyFacetValuesSanitization,
      planFacetOnlyTargetSelfSelection,
      planManagedRangeTargetRemoval,
      planUrlMakeContext,
      planPendingFacetRequests,
      planPriceFacetDomainSanitization,
      resolveRangeKind,
    });
  }

  Object.defineProperty(window, "MarineMaxCoveoFilterRequestPlanner", {
    configurable: false,
    enumerable: false,
    value: Object.freeze({ create: createCoveoFilterRequestPlanner }),
    writable: false,
  });
})();
